/**
 * Frontend HTTP Client
 * Axios-based client with builder pattern and automatic token injection
 */

import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ApiError } from '../errors'

// ============================================================================
// Types
// ============================================================================

export interface HttpClientConfig {
  baseURL: string
  timeout?: number
  headers?: Record<string, string>
}

export interface TokenProvider {
  getToken: () => string | null
  onTokenExpired?: () => void
}

export interface TenantIdProvider {
  getTenantId: () => string | null
}

export interface FactoryIdProvider {
  getFactoryId: () => string | null
}

export interface LocationIdProvider {
  getLocationId: () => string | null
}

export interface LocaleProvider {
  getLocale: () => string | null
}

export interface RequestInterceptor {
  onRequest: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig>
  onRequestError?: (error: AxiosError) => Promise<never>
}

export interface ResponseInterceptor {
  onResponse: (response: AxiosResponse) => AxiosResponse
  onResponseError?: (error: AxiosError) => Promise<never>
}

export type HttpMethod = 'get' | 'post' | 'put' | 'patch' | 'delete'

function isFormDataPayload(data: unknown): data is FormData {
  return typeof FormData !== 'undefined' && data instanceof FormData
}

// ============================================================================
// Capacitor HTTP Adapter (bypass CORS in native WebView)
// ============================================================================

type CapacitorHttpPlugin = {
  request: (options: {
    method: string
    url: string
    headers?: Record<string, string | string[]>
    params?: Record<string, string | string[]>
    data?: unknown
    responseType?: string
  }) => Promise<{
    data: unknown
    status: number
    headers?: Record<string, string>
  }>
}

function getCapacitorHttp(): CapacitorHttpPlugin | null {
  if (typeof window === 'undefined') return null
  const cap = (window as { Capacitor?: { Plugins?: Record<string, unknown>; isNativePlatform?: () => boolean } }).Capacitor
  if (!cap?.Plugins || !cap.Plugins.CapacitorHttp) return null
  if (typeof cap.isNativePlatform === 'function' && !cap.isNativePlatform()) return null
  return cap.Plugins.CapacitorHttp as CapacitorHttpPlugin
}

function isAbsoluteUrl(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

function buildUrl(baseURL: string | undefined, url: string | undefined): string {
  const safeUrl = url ?? ''
  if (!baseURL || isAbsoluteUrl(safeUrl)) return safeUrl
  return `${baseURL.replace(/\/+$/, '')}/${safeUrl.replace(/^\/+/, '')}`
}

type CapacitorHttpRecord = Record<string, string | string[]>

function toPlainRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object') return null

  if ('toJSON' in value && typeof value.toJSON === 'function') {
    const json = value.toJSON()
    return json && typeof json === 'object' ? json as Record<string, unknown> : null
  }

  return value as Record<string, unknown>
}

function stringifyNativeValue(value: unknown): string {
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function normalizeCapacitorHttpRecord(value: unknown): CapacitorHttpRecord | undefined {
  const source = toPlainRecord(value)
  if (!source) return undefined

  const result: CapacitorHttpRecord = {}
  for (const [key, entryValue] of Object.entries(source)) {
    if (entryValue === undefined || entryValue === null) continue

    if (Array.isArray(entryValue)) {
      const values = entryValue
        .filter((item) => item !== undefined && item !== null)
        .map(stringifyNativeValue)
      if (values.length > 0) result[key] = values
      continue
    }

    result[key] = stringifyNativeValue(entryValue)
  }

  return Object.keys(result).length > 0 ? result : undefined
}

function getCapacitorHttpAdapter(): AxiosRequestConfig['adapter'] | undefined {
  const plugin = getCapacitorHttp()
  if (!plugin) return undefined

  return async (config) => {
    const method = String(config.method ?? 'get').toUpperCase()
    const fullUrl = buildUrl(config.baseURL as string | undefined, config.url)
    const response = await plugin.request({
      method,
      url: fullUrl,
      headers: normalizeCapacitorHttpRecord(config.headers),
      params: normalizeCapacitorHttpRecord(config.params),
      data: config.data,
      responseType: config.responseType as string | undefined,
    })

    const axiosResponse: AxiosResponse = {
      data: response.data,
      status: response.status,
      statusText: String(response.status),
      headers: response.headers ?? {},
      config,
      request: null,
    }

    const validateStatus = config.validateStatus
    if (!validateStatus || validateStatus(response.status)) {
      return axiosResponse
    }

    throw new AxiosError(
      `Request failed with status code ${response.status}`,
      undefined,
      config,
      null,
      axiosResponse
    )
  }
}

// ============================================================================
// HTTP Client Class
// ============================================================================

export class HttpClient {
  private readonly instance: AxiosInstance
  private tokenProvider: TokenProvider | null = null
  private tenantIdProvider: TenantIdProvider | null = null
  private factoryIdProvider: FactoryIdProvider | null = null
  private locationIdProvider: LocationIdProvider | null = null
  private localeProvider: LocaleProvider | null = null

  constructor(config: HttpClientConfig) {
    const adapter = getCapacitorHttpAdapter()
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout ?? 30000,
      ...(adapter ? { adapter } : {}),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...config.headers,
      },
    })

    this.instance.interceptors.request.use((requestConfig) => {
      if (!isFormDataPayload(requestConfig.data)) return requestConfig

      const headers = ensureAxiosHeaders(requestConfig.headers)
      headers.delete('Content-Type')
      requestConfig.headers = headers

      return requestConfig
    })
  }

  /**
   * Get the underlying Axios instance
   */
  getAxiosInstance(): AxiosInstance {
    return this.instance
  }

  /**
   * Set token provider for automatic auth injection
   */
  setTokenProvider(provider: TokenProvider): this {
    this.tokenProvider = provider

    // Add request interceptor for token injection
    this.instance.interceptors.request.use(
      (config) => {
        const token = provider.getToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Add response interceptor for 401 handling
    this.instance.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 && provider.onTokenExpired) {
          provider.onTokenExpired()
        }
        return Promise.reject(error)
      }
    )

    return this
  }

  /**
   * Set tenant ID provider for automatic X-Tenant-Id header injection
   * Used by admin users when viewing data as a specific tenant
   */
  setTenantIdProvider(provider: TenantIdProvider): this {
    this.tenantIdProvider = provider

    // Add request interceptor for tenant ID injection
    this.instance.interceptors.request.use(
      (config) => {
        const tenantId = provider.getTenantId()
        if (tenantId) {
          config.headers['X-Tenant-Id'] = tenantId
        } else {
          delete (config.headers as Record<string, unknown>)['X-Tenant-Id']
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    return this
  }

  /**
   * Set factory ID provider for automatic X-Factory-Id header injection
   * Used by gate portal to scope data to a specific factory
   */
  setFactoryIdProvider(provider: FactoryIdProvider): this {
    this.factoryIdProvider = provider

    // Add request interceptor for factory ID injection
    this.instance.interceptors.request.use(
      (config) => {
        const factoryId = provider.getFactoryId()
        if (factoryId) {
          config.headers['X-Factory-Id'] = factoryId
        } else {
          delete (config.headers as Record<string, unknown>)['X-Factory-Id']
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    return this
  }

  /**
   * Set location ID provider for automatic X-Location-Id header injection
   * Used by portals to scope data to a specific location
   */
  setLocationIdProvider(provider: LocationIdProvider): this {
    this.locationIdProvider = provider

    this.instance.interceptors.request.use(
      (config) => {
        const locationId = provider.getLocationId()
        if (locationId) {
          config.headers['X-Location-Id'] = locationId
        } else {
          delete (config.headers as Record<string, unknown>)['X-Location-Id']
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    return this
  }

  /**
   * Set locale provider for automatic locale query parameter injection
   * Ensures Strapi returns localized relation data (e.g. SKU names) in the active locale
   */
  setLocaleProvider(provider: LocaleProvider): this {
    this.localeProvider = provider

    this.instance.interceptors.request.use(
      (config) => {
        const method = String(config.method ?? 'get').toLowerCase()
        if (method !== 'get') return config

        const locale = provider.getLocale()
        if (!locale) return config

        const url = String(config.url ?? '')
        const params = config.params as Record<string, unknown> | undefined
        const hasLocaleInUrl = /[?&]locale=/.test(url)
        const hasLocaleInParams =
          params !== undefined &&
          params !== null &&
          Object.prototype.hasOwnProperty.call(params, 'locale') &&
          params.locale !== undefined &&
          params.locale !== ''

        if (!hasLocaleInUrl && !hasLocaleInParams) {
          config.params = { ...params, locale }
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    return this
  }

  /**
   * Add custom request interceptor
   */
  addRequestInterceptor(interceptor: RequestInterceptor): this {
    this.instance.interceptors.request.use(
      interceptor.onRequest,
      interceptor.onRequestError
    )
    return this
  }

  /**
   * Add custom response interceptor
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): this {
    this.instance.interceptors.response.use(
      interceptor.onResponse,
      interceptor.onResponseError
    )
    return this
  }

  /**
   * GET request
   */
  async get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.get<T>(url, config)
  }

  /**
   * POST request
   */
  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.post<T>(url, data, config)
  }

  /**
   * PUT request
   */
  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.put<T>(url, data, config)
  }

  /**
   * PATCH request
   */
  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.patch<T>(url, data, config)
  }

  /**
   * DELETE request
   */
  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.instance.delete<T>(url, config)
  }
}

// ============================================================================
// HTTP Client Builder
// ============================================================================

export class HttpClientBuilder {
  private baseURL: string = ''
  private timeout: number = 60000
  private headers: Record<string, string> = {}
  private tokenProvider: TokenProvider | null = null
  private tenantIdProvider: TenantIdProvider | null = null
  private factoryIdProvider: FactoryIdProvider | null = null
  private locationIdProvider: LocationIdProvider | null = null
  private localeProvider: LocaleProvider | null = null
  private requestInterceptors: RequestInterceptor[] = []
  private responseInterceptors: ResponseInterceptor[] = []

  /**
   * Set base URL
   */
  withBaseURL(url: string): this {
    this.baseURL = url
    return this
  }

  /**
   * Set timeout in milliseconds
   */
  withTimeout(timeout: number): this {
    this.timeout = timeout
    return this
  }

  /**
   * Add default headers
   */
  withHeaders(headers: Record<string, string>): this {
    this.headers = { ...this.headers, ...headers }
    return this
  }

  /**
   * Set token provider for automatic auth
   */
  withTokenProvider(provider: TokenProvider): this {
    this.tokenProvider = provider
    return this
  }

  /**
   * Set tenant ID provider for automatic X-Tenant-Id header
   */
  withTenantIdProvider(provider: TenantIdProvider): this {
    this.tenantIdProvider = provider
    return this
  }

  /**
   * Set factory ID provider for automatic X-Factory-Id header
   */
  withFactoryIdProvider(provider: FactoryIdProvider): this {
    this.factoryIdProvider = provider
    return this
  }

  /**
   * Set location ID provider for automatic X-Location-Id header
   */
  withLocationIdProvider(provider: LocationIdProvider): this {
    this.locationIdProvider = provider
    return this
  }

  /**
   * Set locale provider for automatic locale injection
   */
  withLocaleProvider(provider: LocaleProvider): this {
    this.localeProvider = provider
    return this
  }

  /**
   * Add request interceptor
   */
  withRequestInterceptor(interceptor: RequestInterceptor): this {
    this.requestInterceptors.push(interceptor)
    return this
  }

  /**
   * Add response interceptor
   */
  withResponseInterceptor(interceptor: ResponseInterceptor): this {
    this.responseInterceptors.push(interceptor)
    return this
  }

  /**
   * Build the HTTP client
   */
  build(): HttpClient {
    const client = new HttpClient({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: this.headers,
    })

    // Set token provider if configured
    if (this.tokenProvider) {
      client.setTokenProvider(this.tokenProvider)
    }

    // Set tenant ID provider if configured
    if (this.tenantIdProvider) {
      client.setTenantIdProvider(this.tenantIdProvider)
    }

    // Set factory ID provider if configured
    if (this.factoryIdProvider) {
      client.setFactoryIdProvider(this.factoryIdProvider)
    }

    // Set location ID provider if configured
    if (this.locationIdProvider) {
      client.setLocationIdProvider(this.locationIdProvider)
    }

    // Set locale provider if configured
    if (this.localeProvider) {
      client.setLocaleProvider(this.localeProvider)
    }

    // Add request interceptors
    for (const interceptor of this.requestInterceptors) {
      client.addRequestInterceptor(interceptor)
    }

    // Add response interceptors
    for (const interceptor of this.responseInterceptors) {
      client.addResponseInterceptor(interceptor)
    }

    return client
  }
}

// ============================================================================
// API Client Definition Helper
// ============================================================================

export interface ApiEndpoint<TResponse = unknown, TRequest = unknown> {
  method: HttpMethod
  path: string | ((params: Record<string, string>) => string)
  transform?: (data: unknown) => TResponse
}

export type ApiDefinition = Record<string, ApiEndpoint>

/**
 * Create a typed API client from endpoint definitions
 */
export function createApiClient<T extends ApiDefinition>(
  httpClient: HttpClient,
  endpoints: T
) {
  type EndpointKeys = keyof T

  const client = {} as {
    [K in EndpointKeys]: T[K]['method'] extends 'get' | 'delete'
      ? (params?: { pathParams?: Record<string, string>; query?: Record<string, unknown> }) => Promise<
          T[K] extends { transform: (data: unknown) => infer R } ? R : unknown
        >
      : (
          data?: unknown,
          params?: { pathParams?: Record<string, string>; query?: Record<string, unknown> }
        ) => Promise<T[K] extends { transform: (data: unknown) => infer R } ? R : unknown>
  }

  for (const [key, endpoint] of Object.entries(endpoints)) {
    const { method, path, transform } = endpoint as ApiEndpoint

    if (method === 'get' || method === 'delete') {
      (client as Record<string, unknown>)[key] = async (params?: {
        pathParams?: Record<string, string>
        query?: Record<string, unknown>
      }) => {
        const url = typeof path === 'function' ? path(params?.pathParams ?? {}) : path
        const response = await httpClient[method](url, { params: params?.query })
        return transform ? transform(response.data) : response.data
      }
    } else {
      (client as Record<string, unknown>)[key] = async (
        data?: unknown,
        params?: { pathParams?: Record<string, string>; query?: Record<string, unknown> }
      ) => {
        const url = typeof path === 'function' ? path(params?.pathParams ?? {}) : path
        const response = await httpClient[method](url, data, { params: params?.query })
        return transform ? transform(response.data) : response.data
      }
    }
  }

  return client
}

// ============================================================================
// Default Client Factory
// ============================================================================

const AUTH_TOKEN_KEY = 'spcc-auth-token'
const STORAGE_MODE_KEY = 'spcc-storage-mode'
const ADMIN_TENANT_KEY = 'admin-selected-tenant'

/**
 * Resolves which storage holds the auth token (sessionStorage or localStorage),
 * matching the logic in token-utils.ts getActiveStorage().
 */
function getAuthStorage(): Storage {
  if (typeof window === 'undefined') return sessionStorage
  const mode = localStorage.getItem(STORAGE_MODE_KEY)
  return mode === 'local' ? localStorage : sessionStorage
}

/**
 * Default token provider that reads from sessionStorage or localStorage
 * depending on the rememberMe mode stored during login.
 */
export function createDefaultTokenProvider(): TokenProvider {
  return {
    getToken: () => {
      if (typeof window === 'undefined') return null
      return getAuthStorage().getItem(AUTH_TOKEN_KEY)
    },
    onTokenExpired: undefined,
  }
}

/**
 * Default tenant ID provider using localStorage
 * Reads the admin-selected-tenant from localStorage (set by TenantSelector)
 */
export function createDefaultTenantIdProvider(): TenantIdProvider {
  return {
    getTenantId: () => {
      if (typeof window === 'undefined') return null
      const stored = localStorage.getItem(ADMIN_TENANT_KEY)
      if (!stored) {
        console.debug('[HttpClient] No tenant in localStorage')
        return null
      }
      try {
        const tenant = JSON.parse(stored) as { id?: string; documentId?: string }
        // Prefer documentId for Strapi v5, fallback to id
        const tenantId = tenant.documentId ?? tenant.id ?? null
        console.debug('[HttpClient] Tenant ID from localStorage:', tenantId)
        return tenantId
      } catch {
        console.debug('[HttpClient] Failed to parse tenant from localStorage')
        return null
      }
    },
  }
}

const LOCALE_KEY = 'locale'
const I18NEXT_LNG_KEY = 'i18nextLng'

/**
 * Default locale provider using localStorage
 * Reads the active locale set by i18n provider (en, ar, ur)
 */
export function createDefaultLocaleProvider(): LocaleProvider {
  return {
    getLocale: () => {
      if (typeof window === 'undefined') return null
      return localStorage.getItem(LOCALE_KEY) || localStorage.getItem(I18NEXT_LNG_KEY) || null
    },
  }
}

/**
 * Get gateway URL from environment
 * Exported for use in auth-api and other modules that need unauthenticated clients
 *
 * Supports both:
 * - Next.js: process.env.VITE_GATEWAY_URL, NEXT_PUBLIC_API_URL, NEXT_PUBLIC_STRAPI_URL (from next.config env block)
 * - Vite: import.meta.env.VITE_GATEWAY_URL, VITE_API_URL, or VITE_STRAPI_URL
 */
export function getGatewayUrl(): string {
  // Next.js (process.env) - ecommerce-app and other Next.js apps
  if (typeof process !== 'undefined' && process.env) {
    const url =
      process.env.VITE_GATEWAY_URL ||
      process.env.NEXT_PUBLIC_API_URL ||
      process.env.NEXT_PUBLIC_STRAPI_URL ||
      process.env.NEXT_PUBLIC_GATEWAY_URL
    if (url && typeof url === 'string' && url.trim() !== '') {
      return url.replace(/\/+$/, '')
    }
  }
  // Vite (import.meta.env) - customer-portal, office-portal, admin-portal, etc.
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    const env = import.meta.env as Record<string, string | boolean | undefined>
    const url = env.VITE_GATEWAY_URL || env.VITE_API_URL || env.VITE_STRAPI_URL
    if (url && typeof url === 'string' && url.trim() !== '') {
      return url.replace(/\/+$/, '')
    }
  }
  return 'http://localhost:8000'
}

/**
 * Get Kong API key from environment
 * Exported for use in auth-api and other modules that need unauthenticated clients
 *
 * Supports both Next.js (process.env) and Vite (import.meta.env)
 */
export function getApiKey(): string | null {
  // Next.js (process.env)
  if (typeof process !== 'undefined' && process.env) {
    const key = process.env.VITE_API_KEY || process.env.NEXT_PUBLIC_API_KEY
    if (key && typeof key === 'string' && key.trim() !== '') return key
  }
  // Vite (import.meta.env)
  if (typeof window !== 'undefined') {
    const apiKey = import.meta?.env?.VITE_API_KEY
    if (apiKey) return String(apiKey)
  }
  return null
}

function fallbackUuidV4(): string {
  // RFC4122 v4-ish fallback when crypto.randomUUID is unavailable.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16)
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8
    return value.toString(16)
  })
}

function generateRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID()
  }
  return fallbackUuidV4()
}

function getHeaderValue(headers: any, name: string): string | undefined {
  if (!headers) return undefined
  if (typeof headers.get === 'function') {
    const value = headers.get(name)
    if (typeof value === 'string') return value
    return undefined
  }
  const direct = headers[name]
  if (typeof direct === 'string') return direct
  const lower = headers[name.toLowerCase()]
  if (typeof lower === 'string') return lower
  return undefined
}

function ensureAxiosHeaders(headers: InternalAxiosRequestConfig['headers']): AxiosHeaders {
  if (headers instanceof AxiosHeaders) return headers
  return AxiosHeaders.from(headers ?? {})
}

// Singleton instance
let defaultHttpClient: HttpClient | null = null

/**
 * Get the default HTTP client configured for the gateway
 */
export function getHttpClient(): HttpClient {
  if (!defaultHttpClient) {
    const builder = new HttpClientBuilder()
      .withBaseURL(getGatewayUrl())
      .withTimeout(60000)
      .withTokenProvider(createDefaultTokenProvider())
      .withTenantIdProvider(createDefaultTenantIdProvider())
      .withLocaleProvider(createDefaultLocaleProvider())
      .withRequestInterceptor({
        onRequest: (config) => {
          const headers = ensureAxiosHeaders(config.headers)
          config.headers = headers

          // Generate a trace id if one wasn't provided by the edge/gateway.
          if (!getHeaderValue(headers, 'x-trace-id')) {
            headers.set('x-trace-id', generateRequestId())
          }

          // Ensure idempotency key exists for mutation requests.
          const method = String(config.method ?? 'get').toLowerCase()
          if (method === 'post' || method === 'put' || method === 'patch') {
            if (!getHeaderValue(headers, 'Idempotency-Key')) {
              headers.set('Idempotency-Key', generateRequestId())
            }
          }

          return config
        },
      })
      .withResponseInterceptor({
        onResponse: (response) => response,
        onResponseError: async (error: AxiosError) => {
          // Extract the Strapi error. Rejecting with an ApiError rather than a
          // bare Error keeps the status and the `details` payload an endpoint
          // returns alongside a failure — flattening it here left every caller
          // with the message and nothing else.
          const data = error.response?.data as
            | {
                error?: {
                  message?: string
                  name?: string
                  details?: Record<string, unknown>
                }
              }
            | undefined
          const message = data?.error?.message || error.message
          return Promise.reject(
            new ApiError(
              message,
              error.response?.status,
              data?.error?.name,
              data?.error?.details
            )
          )
        },
      })

    // Add Kong API key if available
    const apiKey = getApiKey()
    if (apiKey) {
      builder.withHeaders({ 'X-API-Key': apiKey })
    }

    defaultHttpClient = builder.build()
  }
  return defaultHttpClient
}

/**
 * Reset the default client (useful for testing)
 */
export function resetHttpClient(): void {
  defaultHttpClient = null
}

/**
 * Configure tenant ID provider on the default HTTP client
 * Should be called after the Redux store is initialized
 *
 * @example
 * // In store/index.ts after store creation:
 * import { configureTenantIdProvider } from '@spcc/core'
 * import { selectTenantContext } from '@spcc/domains/admin-context'
 *
 * configureTenantIdProvider({
 *   getTenantId: () => selectTenantContext(store.getState())
 * })
 */
export function configureTenantIdProvider(provider: TenantIdProvider): void {
  const client = getHttpClient()
  client.setTenantIdProvider(provider)
}

/**
 * Configure factory ID provider on the default HTTP client
 * Should be called after the Redux store is initialized
 * Used by Gate Portal to scope API requests to a specific factory
 *
 * @example
 * // In store/index.ts after store creation:
 * import { configureFactoryIdProvider } from '@spcc/core'
 * import { selectFactoryContext } from '@spcc/domains/factories'
 *
 * configureFactoryIdProvider({
 *   getFactoryId: () => selectFactoryContext(store.getState())
 * })
 */
export function configureFactoryIdProvider(provider: FactoryIdProvider): void {
  const client = getHttpClient()
  client.setFactoryIdProvider(provider)
}

/**
 * Configure location ID provider on the default HTTP client
 * Should be called after the Redux store is initialized
 * Used by portals to scope API requests to a specific location
 *
 * @example
 * import { configureLocationIdProvider } from '@spcc/core'
 * import { selectLocationContext } from '@spcc/domains/locations'
 *
 * configureLocationIdProvider({
 *   getLocationId: () => selectLocationContext(store.getState())
 * })
 */
export function configureLocationIdProvider(provider: LocationIdProvider): void {
  const client = getHttpClient()
  client.setLocationIdProvider(provider)
}

/**
 * Configure locale provider on the default HTTP client
 * Should be called after the Redux store is initialized if locale is in Redux,
 * otherwise the default provider reads from localStorage automatically
 *
 * @example
 * import { configureLocaleProvider } from '@spcc/core'
 *
 * configureLocaleProvider({
 *   getLocale: () => i18n.language
 * })
 */
export function configureLocaleProvider(provider: LocaleProvider): void {
  const client = getHttpClient()
  client.setLocaleProvider(provider)
}
